import {
  ActivePlanFamily,
  CanonicalInterval,
  PaymentPlanOption,
  PlanFamily,
  PlanFeatureItem,
  PlanFeatureItemDeprecated,
  PlanModelItem,
  PlanSectionType,
  PlanUsageFeature,
  UserTier,
} from 'src/types';
import { PlanKind } from 'src/types/wallet/models/PlanKind';
import { SVG_SIZE_M, SVG_SIZE_S } from 'src/constants/sizes';
import { Icon, IconName } from 'src/components/Icon';
import { ChatGPTLogo } from 'src/images/logos/chat-gpt';
import { NinjaAgentsOutlinedIcon } from 'src/images/icons/ninjaAgentsOutlined';
import {
  SUPER_AGENT,
  LEARN_MORE_VIDEO_GEN,
  SUPER_AGENT_REASONING,
  AI_MODELS_PRICING,
  PAY_PER_USE,
} from './externalLinks';
import {
  ArrowsClockwise,
  ArrowSquareOut,
  Atom,
  Browsers,
  ChartLine,
  Check,
  Checks,
  CirclesThreePlus,
  Code,
  CodeBlock,
  ComputerTower,
  Files,
  Folder,
  GraduationCap,
  HardDrives,
  Images,
  Key,
  ListMagnifyingGlass,
  PencilSimpleLine,
  Shield,
  Sparkle,
  TextAlignLeft,
  Timer,
  UsersThree,
  Video,
  Waveform,
} from '@phosphor-icons/react';
import { getNumberWithoutDigits } from 'src/utils';
import { PROJECT_NAME, SUPER_PROJECT_NAME } from 'src/constants';
import styles from './constStyles.module.scss';
import { PlanVariant } from 'src/types/wallet/models/PlanVariant';
import { TooltipWithModels } from 'src/pages/PaymentBetaPage/componentsV2/TooltipWithModels';
import { SnEntitlementDetailLimits } from 'src/types/models/SnEntitlementDetailLimits';

export const PAYMENT_ERROR = 'Payment failed';
export const PAYMENT_ERROR_MESSAGE =
  'We’re having trouble processing your payment. Please check your information or try a different payment method.';
export const CREDITS_ADDED_SUCCESS_MESSAGE = 'Credits added successfully';
export const CREDITS_SAVED_SUCCESS_MESSAGE = 'On-demand credit purchased';
export const AUTO_PAY_CANCELED_SUCCESS_MESSAGE = 'Auto-pay cancelled';

export const AB_TEST_PAYMENT_OPTIONS_EVENT = 'ab_test_payment_options_event';

export const MAX_PROMO_TOOLTIP_ID = 'max-promo-tooltip';

export const PLAN_OPTIONS_FOR_FREE: PaymentPlanOption[] = [
  {
    lookup_key: 'pro_monthly_10',
    amount: 1000,
    isDefault: true,
  },
  {
    lookup_key: 'pro_monthly_20',
    amount: 2000,
  },
  {
    lookup_key: 'pro_monthly_30',
    amount: 3000,
  },
];

export const RENEW_EARLY_MONTHLY_OPTIONS: PaymentPlanOption[] = [
  {
    lookup_key: 'pro_monthly_10',
    amount: 1000,
    tasksCount: 600,
  },
  {
    lookup_key: 'pro_monthly_20',
    amount: 2000,
    tasksCount: 1200,
  },
  {
    lookup_key: 'pro_monthly_30',
    amount: 3000,
    tasksCount: 1800,
  },
];

export const RENEW_EARLY_ANNUALLY_OPTIONS: PaymentPlanOption[] = [
  {
    lookup_key: 'pro_annually_84',
    amount: 8400,
    tasksCount: 24000,
    isAnnual: true,
  },
  {
    lookup_key: 'pro_annually_168',
    amount: 16800,
    tasksCount: 48000,
    isAnnual: true,
  },
  {
    lookup_key: 'pro_annually_252',
    amount: 25200,
    tasksCount: 72000,
    isAnnual: true,
  },
];

export const PLAN_FREE_FEATURES = [
  { title: 'Writer', withBorder: false },
  { title: 'Researcher', withBorder: false },
  { title: 'Coder', withBorder: false },
  { title: 'Deep Researcher', withBorder: false },
  { title: 'Image Generator', withBorder: false },
  { title: 'Scheduler', withBorder: true },
  { title: '5 minute video chat sessions', withBorder: false },
  { title: '10 custom conversation tones', withBorder: false },
  { title: 'Email & In-product support', withBorder: false },
];

// TODO(olha): needs clearing and refactoring
export const PRO_MAIN_FEATURES = [
  { title: 'Researcher' },
  { title: 'Coder' },
  { title: 'Writing Advisor' },
  { title: 'Image Generator & Editor' },
  { title: 'Meeting Scheduler' },
  { title: '30 minute avatar video sessions' },
  { title: '10 custom conversation tones' },
  { title: 'Email & In-product support' },
];

export const PRO_ADDITIONAL_FEATURES = [
  { title: '30 minute avatar video sessions' },
  { title: '10 custom conversation tones' },
  { title: 'Email & In-product support' },
];

export const PLAN_PRO_FEATURES = [
  { title: 'Writer', withBorder: false },
  { title: 'Researcher', withBorder: false },
  { title: 'Coder', withBorder: false },
  { title: 'Deep Researcher', withBorder: false },
  { title: 'Image Generator', withBorder: false },
  { title: 'Image Editor', withBorder: false },
  { title: 'Multi-party Scheduler', withBorder: false },
  {
    title: '24+ External models',
    withBorder: true,
    isExternalModels: true,
  },
  {
    title: 'State of art interactive avatars',
    withBorder: false,
    // link: { label: '20 Avatars', href: PAY_PER_USE },
  },
  { title: '10 custom conversation tones', withBorder: false },
  { title: 'Email & In-product support', withBorder: false },
];

export const BASIC_FEATURES = [
  { title: '720p (HD) image generation' },
  { title: 'Creative and professional writing' },
  { title: 'Code generation & debugging' },
  { title: 'Real-time research' },
  { title: 'Individual meeting scheduling' },
];

export const PAYMENTS_TIME_FORMAT = 'h:mm A';
export const PAYMENTS_DATE_FORMAT = 'MMM D, YYYY';
export const PAYMENTS_DATE_TIME_FORMAT = 'MMM D, YYYY h:mm A';

export const REDIRECT_TO_STRIPE_EXP_TIME = 30 * 60 * 1000; // 30m

export const PAYPAL_PAYMENT_IN_PROGRESS_MESSAGE =
  'Your subscription is being updated...';

export const PAYPAL_PAYMENT_IN_PROGRESS_BANNER_TTL_SECONDS = 300;

export const STANDARD_FEATURES = [
  {
    id: '1',
    title: <span>Medium quality image generation</span>,
  },
  {
    id: '2',
    title: <span>1 image aspect ratio</span>,
  },
  {
    id: '3',
    title: <span>Generate podcast</span>,
  },
  {
    id: '4',
    title: <span>Creative and professional writing</span>,
  },
  {
    id: '5',
    title: <span>Code generation and debugging</span>,
  },
  {
    id: '6',
    title: <span>Online research</span>,
  },
];

export const TEAMS_FEATURES = [
  {
    id: '10',
    title: <span className="nj-feature-bold-item">Pay-per-use API</span>,
  },
  {
    id: '11',
    title: (
      <span className="nj-feature-bold-item">
        <span>Pay-per-use with premium AI models</span>
        <span className="nj-feature-secondary-item"> (coming soon)</span>
      </span>
    ),
  },
  {
    id: '12',
    title: <span className="nj-feature-bold-item">Workspace management</span>,
  },
  {
    id: '13',
    title: (
      <span className="nj-feature-bold-item">
        Your business data is never used for training models
      </span>
    ),
  },
  {
    id: '14',
    title: (
      <span className="nj-feature-bold-item">
        <span className="colored">Unlimited</span> access to 20+ external AI
        models *
      </span>
    ),
  },
];

export const PRO_FEATURES = [
  {
    id: '1',
    title: <span>3 image aspect ratios</span>,
  },
  {
    id: '2',
    title: <span>Compare mode</span>,
  },
  {
    id: '3',
    title: <span>Advanced online research</span>,
  },
  {
    id: '4',
    title: (
      <span>File upload analysis up to ~150k words across 35+ file types</span>
    ),
  },
];

export const ULTRA_FEATURES = [
  {
    id: '1',
    title: <span>High quality image generation</span>,
  },
  {
    id: '2',
    title: (
      <div className="space-between">
        <span>
          Video generation
          <span className="nj-beta-label">Beta</span>
        </span>

        <a
          href={LEARN_MORE_VIDEO_GEN}
          target="_blank"
          rel="noreferrer"
          className="nj-plan-section--highlight-link"
        >
          learn more
        </a>
      </div>
    ),
  },
  {
    id: '3',
    title: (
      <span>File upload analysis up to ~1.5M words across 35+ file types</span>
    ),
  },
  {
    id: '4',
    title: (
      <div className="space-between">
        <span>Deep Research 2.0</span>
        <span className="nj-new-label">New</span>
      </div>
    ),
  },
  {
    id: '5',
    title: (
      <div className="space-between">
        <span>SuperAgent Apex</span>

        <a
          href={SUPER_AGENT}
          target="_blank"
          rel="noreferrer"
          className="nj-plan-section--highlight-link"
        >
          learn more
        </a>
      </div>
    ),
  },
  {
    id: '6',
    title: (
      <div>
        <span>SuperAgent-R 2.0</span>
        <a
          href={SUPER_AGENT_REASONING}
          target="_blank"
          rel="noreferrer"
          className="nj-plan-section--highlight-link"
        >
          reasoning model
        </a>
      </div>
    ),
  },
];

export const STANDARD_MODELS = [
  {
    id: '1',
    icon: <NinjaAgentsOutlinedIcon width={SVG_SIZE_M} height={SVG_SIZE_M} />,
    text: <span>Ninja auto-selects text models</span>,
    isShowImageIcon: false,
  },
  {
    id: '2',
    icon: <NinjaAgentsOutlinedIcon width={SVG_SIZE_M} height={SVG_SIZE_M} />,
    text: <span>Ninja auto-selects image models</span>,
    isShowImageIcon: false,
  },
];

export const PRO_MODELS = [
  {
    id: '1',
    icon: <NinjaAgentsOutlinedIcon width={SVG_SIZE_M} height={SVG_SIZE_M} />,
    text: <span>Ninja Flux (FLUX Schnell)</span>,
    isShowImageIcon: false,
  },
  {
    id: '5',
    icon: <Icon type="metaLogoJSX" size={SVG_SIZE_M} />,
    text: <span>Llama 4 Scout</span>,
    isShowImageIcon: false,
  },
  {
    id: '6',
    icon: <Icon type="metaLogoJSX" size={SVG_SIZE_M} />,
    text: <span>Llama 4 Maverick</span>,
    isShowImageIcon: false,
  },
];

export const ULTRA_MODELS = [
  {
    id: '1',
    icon: <NinjaAgentsOutlinedIcon width={SVG_SIZE_M} height={SVG_SIZE_M} />,
    text: <span>Ninja Flux (FLUX 1.1 Pro)</span>,
    isShowImageIcon: false,
  },
  {
    id: '2',
    icon: <ChatGPTLogo size={SVG_SIZE_M} />,
    text: <span>OpenAI GPT Image 1</span>,
    isShowImageIcon: false,
  },
  {
    id: '3',
    icon: <Icon type="bedrockStableLogo" size={SVG_SIZE_M} />,
    text: <span>Stable Diffusion Core</span>,
    isShowImageIcon: false,
  },
  {
    id: '4',
    icon: <Icon type="deepSeekLogo" size={SVG_SIZE_M} />,
    text: <span>DeepSeek R1</span>,
    isShowImageIcon: false,
  },
  {
    id: '5',
    icon: <Icon type="deepSeekLogo" size={SVG_SIZE_M} />,
    text: <span>DeepSeek V3</span>,
    isShowImageIcon: false,
  },
  {
    id: '6',
    icon: <ChatGPTLogo size={SVG_SIZE_M} />,
    text: <span>OpenAI GPT-4.1</span>,
    isShowImageIcon: false,
  },
  {
    id: '10',
    icon: <Icon type="nova" size={SVG_SIZE_M} />,
    text: <span>Amazon Nova Pro</span>,
    isShowImageIcon: false,
  },
  {
    id: '11',
    icon: <Icon type="alibabaLogoJSX" size={SVG_SIZE_M} />,
    text: <span>Alibaba Qwen 3 32B</span>,
    isShowImageIcon: false,
  },
  {
    id: '12',
    icon: <Icon type="xaiLogoJSX" size={SVG_SIZE_M} />,
    text: <span>Grok 4 (Preview)</span>,
    isShowImageIcon: false,
  },
];

export const ULTRA_TEAMS_PREMIUM_MODELS = [
  {
    id: '1',
    icon: <ChatGPTLogo size={SVG_SIZE_M} />,
    text: <span>OpenAI o1</span>,
    isShowImageIcon: false,
  },
  {
    id: '2',
    icon: <ChatGPTLogo size={SVG_SIZE_M} />,
    text: <span>OpenAI o3–mini</span>,
    isShowImageIcon: false,
  },
  {
    id: '3',
    icon: <Icon type="googleGeminiProLogo" size={SVG_SIZE_M} />,
    text: <span>Gemini 2.0 Thinking Model</span>,
    isShowImageIcon: false,
  },
  {
    id: '4',
    icon: <Icon type="bedrockStableLogo" size={SVG_SIZE_M} />,
    text: <span>Stable Image Ultra</span>,
    isShowImageIcon: false,
  },
  {
    id: '5',
    icon: <Icon type="bedrockStableLogo" size={SVG_SIZE_M} />,
    text: <span>Stable Diffusion 3.5 Large</span>,
    isShowImageIcon: false,
  },
  {
    id: '6',
    icon: <Icon type="nova" size={SVG_SIZE_M} />,
    text: <span>Amazon Nova Reel</span>,
    isShowImageIcon: false,
  },
];

export const SUBSCRIPTION_PENDING_CHANGES_NOTIFICATION_MESSAGE =
  'Unable to update plans due to pending subscription changes';

export const APPLE_PAYMENT_ERROR_TEXT =
  'Please manage your subscription through the mobile app or the Amazon Partner Network, as applicable.';

export const APPLE_PAYMENT_UPGRADE_ERROR_TEXT =
  'Please upgrade your plan through the mobile app or the Amazon Partner Network, as applicable.';

export const MY_NINJA_STANDARD_FEATURES: PlanFeatureItemDeprecated[] = [
  {
    id: '1',
    icon: <Icon type="ninjaMainIcon" size={SVG_SIZE_M} />,
    title: <span className="nj-feature-bold-text">Chat Agent</span>,
    isKeyFeature: true,
  },
  {
    id: '2',
    icon: <Check size={SVG_SIZE_M} />,
    title: <span className="nj-feature-bold-text">Unlimited Ninja tasks</span>,
    isKeyFeature: true,
  },
  {
    id: '3',
    icon: <Code size={SVG_SIZE_M} />,
    title: 'Code generation & debug',
  },
  {
    id: '4',
    icon: <Images size={SVG_SIZE_M} />,
    title: 'Image generation',
  },
  {
    id: '5',
    icon: <PencilSimpleLine size={SVG_SIZE_M} />,
    title: 'Creative and professional writing',
  },
  {
    id: '6',
    icon: <ListMagnifyingGlass size={SVG_SIZE_M} />,
    title: 'Fast search',
  },
  {
    id: '7',
    icon: <Waveform size={SVG_SIZE_M} />,
    title: 'Generate podcast',
  },
];

export const MY_NINJA_PRO_FEATURES: PlanFeatureItemDeprecated[] = [
  {
    id: '1',
    icon: <Atom size={SVG_SIZE_M} />,
    title: (
      <span>
        <b>10</b> Deep Research tasks per month
      </span>
    ),
    isKeyFeature: true,
  },
  {
    id: '2',
    icon: <Check size={SVG_SIZE_M} />,
    title: (
      <span>
        <b>5</b> premium text AI models
      </span>
    ),
    description: (
      <span className="nj-feature-column-with-gap padding-top">
        <span className="nj-feature-row">
          <Icon type="ninjaPixProLogo" size={SVG_SIZE_S} />
          <span>Ninja Turbo 1.0</span>
        </span>

        <span className="nj-feature-row">
          <Icon type="chatGPTLogo" size={SVG_SIZE_S} />
          <span>OpenAI GPT 4.1</span>
        </span>

        <span className="nj-feature-row">
          <Icon type="googleGeminiProLogo" size={SVG_SIZE_S} />
          <span>Gemini 2.5 Flash1</span>
        </span>

        <span className="nj-feature-row">
          <Icon type="metaLogoJSX" size={SVG_SIZE_S} />
          <span>Llama 4 Scout</span>
        </span>
      </span>
    ),
    isKeyFeature: true,
  },
  {
    id: '3',
    icon: <Check size={SVG_SIZE_M} />,
    title: (
      <span>
        <b>1</b> premium image AI models
      </span>
    ),
    description: (
      <span className="nj-feature-column-with-gap padding-top">
        <span className="nj-feature-row">
          <Icon type="ninjaOutlined" size={SVG_SIZE_S} />
          <span>FLUX.1 Kontext Pro</span>
        </span>
      </span>
    ),
    isKeyFeature: true,
  },
  {
    id: '4',
    icon: <Check size={SVG_SIZE_M} />,
    title: 'Prompt improver',
  },
  {
    id: '5',
    icon: <Check size={SVG_SIZE_M} />,
    title: 'Compare mode',
  },
  {
    id: '6',
    icon: <Code size={SVG_SIZE_M} />,
    title: 'Code generation & debug',
  },
  {
    id: '7',
    icon: <Images size={SVG_SIZE_M} />,
    title: 'Image generation',
  },
  {
    id: '8',
    icon: <PencilSimpleLine size={SVG_SIZE_M} />,
    title: 'Creative and professional writing',
  },
  {
    id: '9',
    icon: <ListMagnifyingGlass size={SVG_SIZE_M} />,
    title: 'Fast search',
  },
  {
    id: '10',
    icon: <Waveform size={SVG_SIZE_M} />,
    title: 'Generate podcast',
  },
];

const aiModelsIcons: IconName[] = [
  'ninjaOutlined',
  'chatGPTLogo',
  'googleGeminiProLogo',
  'claudeLogo',
  'deepSeekLogo',
  'bedrockStableLogo',
  'metaLogoJSX',
  'nova',
  'alibabaLogoJSX',
  'xaiLogoJSX',
];

const aiModelsFeature = (
  <a
    href={AI_MODELS_PRICING}
    target="_blank"
    rel="noreferrer"
    className="nj-link-external-models-wrapper"
  >
    <div className="nj-feature-column">
      <span className="nj-feature-bold-text">
        Unlimited access to 20+ premium AI models*
      </span>
      <div className="nj-feature-models-list">
        {aiModelsIcons.map((iconType) => (
          <Icon key={iconType} type={iconType} size={SVG_SIZE_S} />
        ))}
      </div>
    </div>
    <ArrowSquareOut size={SVG_SIZE_M} className="nj-feature-link-icon" />
  </a>
);

const payPerUseModelsIcons: IconName[] = [
  'chatGPTLogo',
  'googleGeminiProLogo',
  'bedrockStableLogo',
  'nova',
];

const payPerUseModelsFeature = (
  <a
    href={PAY_PER_USE}
    target="_blank"
    rel="noreferrer"
    className="nj-link-external-models-wrapper"
  >
    <div className="nj-feature-column">
      <span className="nj-feature-bold-text">
        Pay-per-use with premium AI models{' '}
        <span className="nj-feature-secondary-item">(coming soon)</span>
      </span>
      <div className="nj-feature-models-list">
        {payPerUseModelsIcons.map((iconType) => (
          <Icon key={iconType} type={iconType} size={SVG_SIZE_S} />
        ))}
      </div>
    </div>
    <ArrowSquareOut size={SVG_SIZE_M} className="nj-feature-link-icon" />
  </a>
);

/**
 * @deprecated
 */
export const MY_NINJA_ULTRA_FEATURES_VARIANT_A: PlanFeatureItemDeprecated[] = [
  {
    id: '1',
    icon: <Icon type="ninjaMainIcon" size={SVG_SIZE_M} />,
    title: <span className="nj-feature-bold-text">Chat Agent</span>,
    isKeyFeature: true,
  },
  {
    id: '2',
    icon: <Check size={SVG_SIZE_M} />,
    title: <span className="nj-feature-bold-text">Unlimited Ninja tasks</span>,
    isKeyFeature: true,
  },
  {
    id: '3',
    icon: <Check size={SVG_SIZE_M} />,
    title: aiModelsFeature,
    isKeyFeature: true,
  },
  {
    id: '4',
    icon: <Check size={SVG_SIZE_M} />,
    title: 'Prompt improver',
  },
  {
    id: '5',
    icon: <Check size={SVG_SIZE_M} />,
    title: 'Compare mode',
  },
  {
    id: '6',
    icon: <Check size={SVG_SIZE_M} />,
    title: 'Document & code editor',
  },
  {
    id: '7',
    icon: <Atom size={SVG_SIZE_M} />,
    title: 'Deep Research',
  },
  {
    id: '8',
    icon: <Code size={SVG_SIZE_M} />,
    title: 'Code generation & debug',
  },
  {
    id: '9',
    icon: <Images size={SVG_SIZE_M} />,
    title: 'Image generation',
  },
  {
    id: '10',
    icon: <PencilSimpleLine size={SVG_SIZE_M} />,
    title: 'Creative and professional writing',
  },
  {
    id: '11',
    icon: <ListMagnifyingGlass size={SVG_SIZE_M} />,
    title: 'Fast search',
  },
  {
    id: '12',
    icon: <Video size={SVG_SIZE_M} />,
    title: 'Video generation',
  },
  {
    id: '13',
    icon: <Waveform size={SVG_SIZE_M} />,
    title: 'Generate podcast',
  },
];

/**
 * @deprecated
 */
export const MY_NINJA_ULTRA_FEATURES_VARIANT_B: PlanFeatureItemDeprecated[] = [
  {
    id: '1',
    icon: <Atom size={SVG_SIZE_M} />,
    title: (
      <span>
        <b>Unlimited</b> Deep Research
      </span>
    ),
    isKeyFeature: true,
  },
  {
    id: '2',
    icon: <Check size={SVG_SIZE_M} />,
    title: (
      <span>
        <b>20+</b> premium text AI models
      </span>
    ),
    description: (
      <span className="nj-feature-column-with-gap padding-top">
        <span className="nj-feature-row">
          <Icon type="ninjaPixProLogo" size={SVG_SIZE_S} />
          <span>Ninja Reasoning 2.0, Turbo 1.0, Apex 1.0</span>
        </span>

        <span className="nj-feature-row">
          <Icon type="chatGPTLogo" size={SVG_SIZE_S} />
          <span>OpenAI o3, GPT 4.1, GPT 4.1 Mini</span>
        </span>

        <span className="nj-feature-row">
          <Icon type="claudeLogo" size={SVG_SIZE_S} />
          <span>Claude 4 Sonnet</span>
        </span>

        <span className="nj-feature-row">
          <Icon type="googleGeminiProLogo" size={SVG_SIZE_S} />
          <span>Gemini 2.5 Pro, Gemini 2.5 Flash</span>
        </span>

        <span className="nj-feature-row">
          <Icon type="deepSeekLogo" size={SVG_SIZE_S} />
          <span>DeepSeek R1, DeepSeek V3</span>
        </span>

        <span className="nj-feature-row">
          <Icon type="xaiLogoJSX" size={SVG_SIZE_S} />
          <span>Grok 4</span>
        </span>

        <span className="nj-feature-row">
          <Icon type="metaLogoJSX" size={SVG_SIZE_S} />
          <span>Llama 4 Scout</span>
        </span>

        <span className="nj-feature-row">
          <Icon type="alibabaLogoJSX" size={SVG_SIZE_S} />
          <span>Qwen 3 32B (Fast), Qwen 3 235B</span>
        </span>

        <span className="nj-feature-row">
          <Icon type="mistralLogo" size={SVG_SIZE_S} />
          <span>Mistral Large, Mistral Small</span>
        </span>

        <span className="nj-feature-row">
          <Icon type="cohereLogo" size={SVG_SIZE_S} />
          <span>Cohere Command R+, Cohere Command R</span>
        </span>
      </span>
    ),
    isKeyFeature: true,
  },
  {
    id: '3',
    icon: <Check size={SVG_SIZE_M} />,
    title: (
      <span>
        <b>10+</b> premium image & video AI models
      </span>
    ),
    description: (
      <span className="nj-feature-column-with-gap padding-top">
        <span className="nj-feature-row">
          <Icon type="ninjaOutlined" size={SVG_SIZE_S} />
          <span>
            FLUX Schnell, FLUX.1 Kontext Pro,
            <br />
            FLUX 1.1 PRO
          </span>
        </span>

        <span className="nj-feature-row">
          <Icon type="chatGPTLogo" size={SVG_SIZE_S} />
          <span>OpenAI GPT Image 1, DALL·E 3</span>
        </span>

        <span className="nj-feature-row">
          <Icon type="bedrockStableLogo" size={SVG_SIZE_S} />
          <span>Stable Diffusion Core</span>
        </span>

        <span className="nj-feature-row">
          <Icon type="googleGeminiProLogo" size={SVG_SIZE_S} />
          <span>
            Gemini Imagen 4 Ultra, Imagen 4,
            <br />
            Imagen 4 Fast
          </span>
        </span>

        <span className="nj-feature-row">
          <Icon type="nova" size={SVG_SIZE_S} />
          <span>Amazon Nova Canvas, Nova Reel (Video)</span>
        </span>
      </span>
    ),
    isKeyFeature: true,
  },
  {
    id: '4',
    icon: <Check size={SVG_SIZE_M} />,
    title: 'Document & code editor',
  },
  {
    id: '5',
    icon: <Check size={SVG_SIZE_M} />,
    title: 'File upload up to 2M words',
  },
  {
    id: '6',
    icon: <Video size={SVG_SIZE_M} />,
    title: 'Video generation',
  },
];

/**
 * @deprecated
 */
export const getSuperNinjaUltraFeatures = (
  isAnnually?: boolean,
): PlanFeatureItemDeprecated[] => [
  {
    id: '1',
    icon: <Icon type="superNinjaMainIcon" size={SVG_SIZE_M} />,
    title: <span className="nj-feature-bold-text">{SUPER_PROJECT_NAME}</span>,
    description: 'Agent with a dedicated cloud computer',
    isKeyFeature: true,
  },
  {
    id: '2', // do not change this item till you check it in CancelSubscriptionModal
    icon: <Checks size={SVG_SIZE_M} />,
    title: <span className="nj-feature-bold-text">Everything in Ultra</span>,
    isKeyFeature: true,
  },
  {
    id: '3',
    icon: <Check size={SVG_SIZE_M} />,
    title: (
      <span className="nj-feature-bold-text">
        {getNumberWithoutDigits(isAnnually ? 250 * 12 : 250)}{' '}
        {SUPER_PROJECT_NAME} tasks per {isAnnually ? 'year' : 'month'}
      </span>
    ),
    description: (
      <div className="nj-feature-description-column">
        <span>
          ~{getNumberWithoutDigits(isAnnually ? 15000 * 12 : 15000)} -{' '}
          {getNumberWithoutDigits(isAnnually ? 30000 * 12 : 30000)} premium LLM
          calls
        </span>
        <span>
          (
          <b>
            Est. market value: $
            {getNumberWithoutDigits(isAnnually ? 400 * 12 : 400)}-$
            {getNumberWithoutDigits(isAnnually ? 800 * 12 : 800)}
          </b>
          , based on industry comparison)
        </span>
      </div>
    ),
    isKeyFeature: true,
  },
  // ...SUPER_NINJA_COMMON_FEATURES,
];

/**
 * @deprecated
 */
export const MY_NINJA_BUSINESS_ULTRA_FEATURES_VARIANT_A: PlanFeatureItemDeprecated[] =
  [
    {
      id: '1',
      icon: <Icon type="ninjaMainIcon" size={SVG_SIZE_M} />,
      title: <span className="nj-feature-bold-text">Chat Agent</span>,
      isKeyFeature: true,
    },
    {
      id: '2',
      icon: <Check size={SVG_SIZE_M} />,
      title: (
        <span className="nj-feature-bold-text">Unlimited Ninja tasks</span>
      ),
      isKeyFeature: true,
    },
    {
      id: '3',
      icon: <Check size={SVG_SIZE_M} />,
      title: <span className="nj-feature-bold-text">Pay-per-use API</span>,
      isKeyFeature: true,
    },
    {
      id: '4',
      icon: <Check size={SVG_SIZE_M} />,
      title: <span className="nj-feature-bold-text">Workspace management</span>,
    },
    {
      id: '5',
      icon: <Check size={SVG_SIZE_M} />,
      title: (
        <span className="nj-feature-bold-text">
          Your data is never used for training models
        </span>
      ),
    },
    {
      id: '6',
      icon: <Check size={SVG_SIZE_M} />,
      title: aiModelsFeature,
    },
    {
      id: '7',
      icon: <Check size={SVG_SIZE_M} />,
      title: payPerUseModelsFeature,
    },
    {
      id: '8',
      icon: <Check size={SVG_SIZE_M} />,
      title: 'Prompt improver',
    },
    {
      id: '9',
      icon: <Check size={SVG_SIZE_M} />,
      title: 'Compare mode',
    },
    {
      id: '10',
      icon: <Check size={SVG_SIZE_M} />,
      title: 'Document & code editor',
    },
    {
      id: '11',
      icon: <Atom size={SVG_SIZE_M} />,
      title: 'Deep Research',
    },
    {
      id: '12',
      icon: <Code size={SVG_SIZE_M} />,
      title: 'Code generation & debug',
    },
    {
      id: '13',
      icon: <Images size={SVG_SIZE_M} />,
      title: 'Image generation',
    },
    {
      id: '14',
      icon: <PencilSimpleLine size={SVG_SIZE_M} />,
      title: 'Creative and professional writing',
    },
    {
      id: '15',
      icon: <ListMagnifyingGlass size={SVG_SIZE_M} />,
      title: 'Fast search',
    },
    {
      id: '16',
      icon: <Video size={SVG_SIZE_M} />,
      title: 'Video generation',
    },
    {
      id: '17',
      icon: <Waveform size={SVG_SIZE_M} />,
      title: 'Generate podcast',
    },
  ];

/**
 * @deprecated
 */
export const MY_NINJA_BUSINESS_ULTRA_FEATURES_VARIANT_B: PlanFeatureItemDeprecated[] =
  [
    {
      id: '1',
      icon: <Check size={SVG_SIZE_M} />,
      title: (
        <span>
          Everything in <u>Ultra</u> – Unlimited Deep Research, access to 20+ AI
          models & 10 AI agents
        </span>
      ),
      isKeyFeature: true,
    },
    {
      id: '2',
      icon: <Check size={SVG_SIZE_M} />,
      title: 'Pay-per-use API',
      isKeyFeature: true,
    },
    {
      id: '3',
      icon: <Check size={SVG_SIZE_M} />,
      title: 'Pay-per-use with premium AI models',
      description: (
        <span className="nj-feature-column-with-gap">
          <span className="nj-feature-secondary-item">(coming soon)</span>

          <span className="nj-feature-row">
            <Icon type="chatGPTLogo" size={SVG_SIZE_S} />
            <span>OpenAI o1, OpenAI o3–mini</span>
          </span>

          <span className="nj-feature-row">
            <Icon type="googleGeminiProLogo" size={SVG_SIZE_S} />
            <span>Gemini 2.0 Thinking Model</span>
          </span>

          <span className="nj-feature-row">
            <Icon type="bedrockStableLogo" size={SVG_SIZE_S} />
            <span>Stable Image Ultra, Stable Diffusion 3.5 Large</span>
          </span>

          <span className="nj-feature-row">
            <Icon type="nova" size={SVG_SIZE_S} />
            <span>Amazon Nova Reel (Video)</span>
          </span>
        </span>
      ),
      isKeyFeature: true,
    },
    {
      id: '4',
      icon: <Check size={SVG_SIZE_M} />,
      title: 'Workspace management',
    },
    {
      id: '5',
      icon: <Check size={SVG_SIZE_M} />,
      title: 'No training on your data',
    },
  ];

export const getSuperNinjaBusinessFeatures = (
  isAnnually?: boolean,
): PlanFeatureItemDeprecated[] => [
  {
    id: '1',
    icon: <Icon type="superNinjaMainIcon" size={SVG_SIZE_M} />,
    title: <span className="nj-feature-bold-text">{SUPER_PROJECT_NAME}</span>,
    description: 'Agent with a dedicated cloud computer',
    isKeyFeature: true,
  },
  {
    id: '2',
    icon: <Checks size={SVG_SIZE_M} />,
    title: <span className="nj-feature-bold-text">Everything in Business</span>,
    isKeyFeature: true,
  },
  {
    id: '3',
    icon: <Check size={SVG_SIZE_M} />,
    title: (
      <span className="nj-feature-bold-text">
        {getNumberWithoutDigits(isAnnually ? 300 * 12 : 300)}{' '}
        {SUPER_PROJECT_NAME} tasks per seat
      </span>
    ),
    description: (
      <div className="nj-feature-description-column">
        <span>
          ~{getNumberWithoutDigits(isAnnually ? 30000 * 12 : 30000)} -{' '}
          {getNumberWithoutDigits(isAnnually ? 60000 * 12 : 60000)} premium LLM
          calls
        </span>
        <span>
          (
          <b>
            Est. market value: $
            {getNumberWithoutDigits(isAnnually ? 800 * 12 : 800)}-$
            {getNumberWithoutDigits(isAnnually ? 1600 * 12 : 1600)}
          </b>
          , based on industry comparison)
        </span>
      </div>
    ),
    isKeyFeature: true,
  },
  // ...SUPER_NINJA_COMMON_FEATURES,
];

/**
 * @deprecated
 */
export const SUPERNINJA_USAGE_DATASET: Partial<
  Record<
    PlanSectionType,
    {
      requestNumber: number;
      concurrentTasksNumber: number;
      minLLMCalls: number;
      maxLLMCalls: number;
      minMarketValue: number;
      maxMarketValue: number;
    }
  >
> = {
  SuperNinja_Ultra_10: {
    requestNumber: 50,
    concurrentTasksNumber: 1,
    minLLMCalls: 3000,
    maxLLMCalls: 6000,
    minMarketValue: 80,
    maxMarketValue: 160,
  },
  SuperNinja_Ultra_20: {
    requestNumber: 100,
    concurrentTasksNumber: 2,
    minLLMCalls: 6000,
    maxLLMCalls: 12000,
    minMarketValue: 160,
    maxMarketValue: 320,
  },
  SuperNinja_Ultra: {
    requestNumber: 250,
    concurrentTasksNumber: 5,
    minLLMCalls: 15000,
    maxLLMCalls: 30000,
    minMarketValue: 400,
    maxMarketValue: 800,
  },
  Teams_SuperNinja_Ultra_10: {
    requestNumber: 60,
    concurrentTasksNumber: 1,
    minLLMCalls: 3000,
    maxLLMCalls: 6000,
    minMarketValue: 80,
    maxMarketValue: 160,
  },
  Teams_SuperNinja_Ultra_20: {
    requestNumber: 125,
    concurrentTasksNumber: 2,
    minLLMCalls: 6000,
    maxLLMCalls: 12000,
    minMarketValue: 160,
    maxMarketValue: 320,
  },
  Teams_SuperNinja_Ultra: {
    requestNumber: 300,
    concurrentTasksNumber: 5,
    minLLMCalls: 15000,
    maxLLMCalls: 30000,
    minMarketValue: 400,
    maxMarketValue: 800,
  },
};

// Payment 2.0. All features above are deprecated. -------------

export const LEGACY_PLAN_KINDS = [
  PlanKind.PRO_MONTHLY,
  PlanKind.PRO_ANNUALLY,
  PlanKind.ULTRA_MONTHLY,
  PlanKind.ULTRA_ANNUALLY,
  PlanKind.STANDARD_MONTHLY,
  PlanKind.STANDARD_ANNUALLY,
  PlanKind.TEAMS_PRO_MONTHLY,
  PlanKind.TEAMS_PRO_ANNUALLY,
  PlanKind.TEAMS_ULTRA_MONTHLY,
  PlanKind.TEAMS_ULTRA_ANNUALLY,
  PlanKind.TEAMS_SUPERNINJA_ULTRA_MONTHLY_10,
  PlanKind.TEAMS_SUPERNINJA_ULTRA_MONTHLY_20,
  PlanKind.TEAMS_SUPERNINJA_ULTRA_MONTHLY,
  PlanKind.TEAMS_SUPERNINJA_ULTRA_ANNUALLY_10,
  PlanKind.TEAMS_SUPERNINJA_ULTRA_ANNUALLY_20,
  PlanKind.TEAMS_SUPERNINJA_ULTRA_ANNUALLY,
  PlanKind.TEAMS_STANDARD_MONTHLY,
  PlanKind.TEAMS_STANDARD_ANNUALLY,
  PlanKind.SUPERNINJA_ULTRA_MONTHLY_10,
  PlanKind.SUPERNINJA_ULTRA_MONTHLY_20,
  PlanKind.SUPERNINJA_ULTRA_MONTHLY,
  PlanKind.SUPERNINJA_ULTRA_ANNUALLY_10,
  PlanKind.SUPERNINJA_ULTRA_ANNUALLY_20,
  PlanKind.SUPERNINJA_ULTRA_ANNUALLY,
];

export const PLAN_SELECTION_KINDS_MAP: Record<
  Exclude<PlanFamily, PlanFamily.UNDEFINED | PlanFamily.CREDIT>,
  Record<CanonicalInterval.ANNUALLY | CanonicalInterval.MONTHLY, PlanKind[]>
> = {
  [PlanFamily.PRO]: {
    [CanonicalInterval.MONTHLY]: [
      PlanKind.PRO_10_MONTHLY,
      PlanKind.PRO_30_MONTHLY,
    ],
    [CanonicalInterval.ANNUALLY]: [
      PlanKind.PRO_10_ANNUALLY,
      PlanKind.PRO_30_ANNUALLY,
    ],
  },
  [PlanFamily.MAX]: {
    [CanonicalInterval.MONTHLY]: [
      PlanKind.MAX_10_MONTHLY,
      PlanKind.MAX_20_MONTHLY,
      PlanKind.MAX_30_MONTHLY,
    ],
    [CanonicalInterval.ANNUALLY]: [
      PlanKind.MAX_10_ANNUALLY,
      PlanKind.MAX_20_ANNUALLY,
      PlanKind.MAX_30_ANNUALLY,
    ],
  },
  [PlanFamily.BUSINESS]: {
    [CanonicalInterval.MONTHLY]: [
      PlanKind.BUSINESS_10_MONTHLY,
      PlanKind.BUSINESS_20_MONTHLY,
      PlanKind.BUSINESS_30_MONTHLY,
    ],
    [CanonicalInterval.ANNUALLY]: [
      PlanKind.BUSINESS_10_ANNUALLY,
      PlanKind.BUSINESS_20_ANNUALLY,
      PlanKind.BUSINESS_30_ANNUALLY,
    ],
  },
} satisfies Record<
  ActivePlanFamily,
  Record<CanonicalInterval.ANNUALLY | CanonicalInterval.MONTHLY, PlanKind[]>
>;

export const NEW_PLAN_SELECTION_KINDS_MAP: Record<
  Exclude<PlanFamily, PlanFamily.UNDEFINED | PlanFamily.CREDIT>,
  Record<CanonicalInterval.ANNUALLY | CanonicalInterval.MONTHLY, PlanKind[]>
> = {
  [PlanFamily.PRO]: {
    [CanonicalInterval.MONTHLY]: [
      PlanKind.PRO_10_MONTHLY,
      PlanKind.PRO_30_MONTHLY,
    ],
    [CanonicalInterval.ANNUALLY]: [
      PlanKind.PRO_10_ANNUALLY,
      PlanKind.PRO_30_ANNUALLY,
    ],
  },
  [PlanFamily.MAX]: {
    [CanonicalInterval.MONTHLY]: [
      PlanKind.MAX_10_MONTHLY,
      PlanKind.MAX_30_MONTHLY,
    ],
    [CanonicalInterval.ANNUALLY]: [
      PlanKind.MAX_10_ANNUALLY,
      PlanKind.MAX_30_ANNUALLY,
    ],
  },
  [PlanFamily.BUSINESS]: {
    [CanonicalInterval.MONTHLY]: [
      PlanKind.BUSINESS_10_MONTHLY,
      PlanKind.BUSINESS_30_MONTHLY,
    ],
    [CanonicalInterval.ANNUALLY]: [
      PlanKind.BUSINESS_10_ANNUALLY,
      PlanKind.BUSINESS_30_ANNUALLY,
    ],
  },
} satisfies Record<
  ActivePlanFamily,
  Record<CanonicalInterval.ANNUALLY | CanonicalInterval.MONTHLY, PlanKind[]>
>;

export const DEFAULT_BUY_MONTHLY_PLAN_KIND_MAP: Record<
  ActivePlanFamily,
  PlanKind
> = {
  [PlanFamily.PRO]: PlanKind.PRO_30_MONTHLY,
  [PlanFamily.MAX]: PlanKind.MAX_10_MONTHLY,
  [PlanFamily.BUSINESS]: PlanKind.BUSINESS_10_MONTHLY,
};

export const DEFAULT_BUY_ANNUALLY_PLAN_KIND_MAP: Record<
  ActivePlanFamily,
  PlanKind
> = {
  [PlanFamily.PRO]: PlanKind.PRO_30_ANNUALLY,
  [PlanFamily.MAX]: PlanKind.MAX_10_ANNUALLY,
  [PlanFamily.BUSINESS]: PlanKind.BUSINESS_10_ANNUALLY,
};

export const DEFAULT_UPDATE_MONTHLY_PLAN_KIND_MAP: Record<
  ActivePlanFamily,
  PlanKind
> = {
  [PlanFamily.PRO]: PlanKind.PRO_30_MONTHLY,
  [PlanFamily.MAX]: PlanKind.MAX_30_MONTHLY,
  [PlanFamily.BUSINESS]: PlanKind.BUSINESS_30_MONTHLY,
};

export const DEFAULT_UPDATE_ANNUALLY_PLAN_KIND_MAP: Record<
  ActivePlanFamily,
  PlanKind
> = {
  [PlanFamily.PRO]: PlanKind.PRO_30_ANNUALLY,
  [PlanFamily.MAX]: PlanKind.MAX_30_ANNUALLY,
  [PlanFamily.BUSINESS]: PlanKind.BUSINESS_30_ANNUALLY,
};

export const SPECIAL_OFFER_PLAN_KIND = [
  PlanKind.MAX_30_MONTHLY,
  PlanKind.MAX_30_ANNUALLY,
  PlanKind.BUSINESS_30_MONTHLY,
  PlanKind.BUSINESS_30_ANNUALLY,
];

const BASE_MAX_USAGE_DATASET = {
  '10': {
    requestCount: 150,
    concurrentTasksCount: 1,
    deepResearchType: 'World’s Fastest',
    minMarketValue: 75,
    maxMarketValue: 300,
  },
  '20': {
    requestCount: 300,
    concurrentTasksCount: 2,
    deepResearchType: 'World’s Fastest',
    minMarketValue: 150,
    maxMarketValue: 600,
  },
  unlimited: {
    requestCount: 'Unlimited',
    concurrentTasksCount: 5,
    deepResearchType: 'World’s Fastest',
    minMarketValue: 2000,
    maxMarketValue: 2500,
  },
} satisfies Record<'10' | '20' | 'unlimited', PlanUsageFeature>;

export const NEW_BASE_MAX_USAGE_DATASET = {
  '5': {
    requestCount: 40,
    concurrentTasksCount: 1,
    deepResearchType: 'World’s Fastest',
    minMarketValue: 75,
    maxMarketValue: 300,
  },
  '10': {
    requestCount: 80,
    concurrentTasksCount: 2,
    deepResearchType: 'World’s Fastest',
    minMarketValue: 75,
    maxMarketValue: 300,
  },
  '20': {
    requestCount: 160,
    concurrentTasksCount: 5,
    deepResearchType: 'World’s Fastest',
    minMarketValue: 150,
    maxMarketValue: 600,
  },
  unlimited: {
    requestCount: 'Unlimited',
    concurrentTasksCount: 10,
    deepResearchType: 'World’s Fastest',
    minMarketValue: 2000,
    maxMarketValue: 2500,
  },
  'b-10': {
    requestCount: 80,
    concurrentTasksCount: 2,
    deepResearchType: 'World’s Fastest',
    minMarketValue: 75,
    maxMarketValue: 300,
  },
  'b-20': {
    requestCount: 160,
    concurrentTasksCount: 5,
    deepResearchType: 'World’s Fastest',
    minMarketValue: 150,
    maxMarketValue: 600,
  },
  'b-unlimited': {
    requestCount: 'Unlimited',
    concurrentTasksCount: 10,
    deepResearchType: 'World’s Fastest',
    minMarketValue: 2000,
    maxMarketValue: 2500,
  },
} satisfies Record<
  '5' | '10' | '20' | 'unlimited' | 'b-10' | 'b-20' | 'b-unlimited',
  PlanUsageFeature
>;

export const MAX_USAGE_DATASET: Partial<Record<PlanKind, PlanUsageFeature>> = {
  [PlanKind.MAX_10_MONTHLY]: BASE_MAX_USAGE_DATASET['10'],
  [PlanKind.MAX_20_MONTHLY]: BASE_MAX_USAGE_DATASET['20'],
  [PlanKind.MAX_30_MONTHLY]: BASE_MAX_USAGE_DATASET['unlimited'],
  [PlanKind.MAX_10_ANNUALLY]: BASE_MAX_USAGE_DATASET['10'],
  [PlanKind.MAX_20_ANNUALLY]: BASE_MAX_USAGE_DATASET['20'],
  [PlanKind.MAX_30_ANNUALLY]: BASE_MAX_USAGE_DATASET['unlimited'],
  [PlanKind.BUSINESS_10_MONTHLY]: BASE_MAX_USAGE_DATASET['10'],
  [PlanKind.BUSINESS_20_MONTHLY]: BASE_MAX_USAGE_DATASET['20'],
  [PlanKind.BUSINESS_30_MONTHLY]: BASE_MAX_USAGE_DATASET['unlimited'],
  [PlanKind.BUSINESS_10_ANNUALLY]: BASE_MAX_USAGE_DATASET['10'],
  [PlanKind.BUSINESS_20_ANNUALLY]: BASE_MAX_USAGE_DATASET['20'],
  [PlanKind.BUSINESS_30_ANNUALLY]: BASE_MAX_USAGE_DATASET['unlimited'],
};

export const NEW_MAX_USAGE_DATASET: Partial<
  Record<PlanKind, PlanUsageFeature>
> = {
  [PlanKind.MAX_05_MONTHLY]: NEW_BASE_MAX_USAGE_DATASET['5'],
  [PlanKind.MAX_05_ANNUALLY]: NEW_BASE_MAX_USAGE_DATASET['5'],
  [PlanKind.MAX_10_MONTHLY]: NEW_BASE_MAX_USAGE_DATASET['10'],
  [PlanKind.MAX_20_MONTHLY]: NEW_BASE_MAX_USAGE_DATASET['20'],
  [PlanKind.MAX_30_MONTHLY]: NEW_BASE_MAX_USAGE_DATASET['unlimited'],
  [PlanKind.MAX_10_ANNUALLY]: NEW_BASE_MAX_USAGE_DATASET['10'],
  [PlanKind.MAX_20_ANNUALLY]: NEW_BASE_MAX_USAGE_DATASET['20'],
  [PlanKind.MAX_30_ANNUALLY]: NEW_BASE_MAX_USAGE_DATASET['unlimited'],
  [PlanKind.BUSINESS_10_MONTHLY]: NEW_BASE_MAX_USAGE_DATASET['b-10'],
  [PlanKind.BUSINESS_20_MONTHLY]: NEW_BASE_MAX_USAGE_DATASET['b-20'],
  [PlanKind.BUSINESS_30_MONTHLY]: NEW_BASE_MAX_USAGE_DATASET['b-unlimited'],
  [PlanKind.BUSINESS_10_ANNUALLY]: NEW_BASE_MAX_USAGE_DATASET['b-10'],
  [PlanKind.BUSINESS_20_ANNUALLY]: NEW_BASE_MAX_USAGE_DATASET['b-20'],
  [PlanKind.BUSINESS_30_ANNUALLY]: NEW_BASE_MAX_USAGE_DATASET['b-unlimited'],
};

export const PLAN_KIND_TITLE_MAP: Partial<
  Record<PlanKind, { fullTitle: string; shortTitle: string }>
> = {
  [PlanKind.PRO_10_MONTHLY]: {
    fullTitle: `${PROJECT_NAME} Standard`,
    shortTitle: 'Standard',
  },
  [PlanKind.PRO_10_ANNUALLY]: {
    fullTitle: `${PROJECT_NAME} Standard`,
    shortTitle: 'Standard',
  },
  [PlanKind.PRO_30_MONTHLY]: {
    fullTitle: `${PROJECT_NAME} Ultra`,
    shortTitle: 'Ultra',
  },
  [PlanKind.PRO_30_ANNUALLY]: {
    fullTitle: `${PROJECT_NAME} Ultra`,
    shortTitle: 'Ultra',
  },
  [PlanKind.MAX_05_MONTHLY]: {
    fullTitle: `${SUPER_PROJECT_NAME} Standard`,
    shortTitle: 'Standard',
  },
  [PlanKind.MAX_05_ANNUALLY]: {
    fullTitle: `${SUPER_PROJECT_NAME} Standard`,
    shortTitle: 'Standard',
  },
  [PlanKind.MAX_10_MONTHLY]: {
    fullTitle: `${SUPER_PROJECT_NAME} Pro`,
    shortTitle: 'Pro',
  },
  [PlanKind.MAX_10_ANNUALLY]: {
    fullTitle: `${SUPER_PROJECT_NAME} Pro`,
    shortTitle: 'Pro',
  },
  [PlanKind.MAX_20_MONTHLY]: {
    fullTitle: `${SUPER_PROJECT_NAME} Ultra`,
    shortTitle: 'Ultra',
  },
  [PlanKind.MAX_20_ANNUALLY]: {
    fullTitle: `${SUPER_PROJECT_NAME} Ultra`,
    shortTitle: 'Ultra',
  },
  [PlanKind.MAX_30_MONTHLY]: {
    fullTitle: `${SUPER_PROJECT_NAME} Max`,
    shortTitle: 'Max',
  },
  [PlanKind.MAX_30_ANNUALLY]: {
    fullTitle: `${SUPER_PROJECT_NAME} Max`,
    shortTitle: 'Max',
  },
  [PlanKind.BUSINESS_10_MONTHLY]: {
    fullTitle: 'Business Pro',
    shortTitle: 'Pro',
  },
  [PlanKind.BUSINESS_10_ANNUALLY]: {
    fullTitle: 'Business Pro',
    shortTitle: 'Pro',
  },
  [PlanKind.BUSINESS_20_MONTHLY]: {
    fullTitle: 'Business Ultra',
    shortTitle: 'Ultra',
  },
  [PlanKind.BUSINESS_20_ANNUALLY]: {
    fullTitle: 'Business Ultra',
    shortTitle: 'Ultra',
  },
  [PlanKind.BUSINESS_30_MONTHLY]: {
    fullTitle: 'Business Max',
    shortTitle: 'Max',
  },
  [PlanKind.BUSINESS_30_ANNUALLY]: {
    fullTitle: 'Business Max',
    shortTitle: 'Max',
  },
};

export const TIER_TITLE_MAP: Partial<Record<UserTier, string>> = {
  [UserTier.STANDARD]: `${PROJECT_NAME} Standard`,
  [UserTier.ULTRA]: `${PROJECT_NAME} Ultra`,
  [UserTier.SUPERNINJA_STANDARD]: `${SUPER_PROJECT_NAME} Standard`,
  [UserTier.SUPERNINJA_PRO]: `${SUPER_PROJECT_NAME} Pro`,
  [UserTier.SUPERNINJA_ULTRA]: `${SUPER_PROJECT_NAME} Ultra`,
  [UserTier.SUPERNINJA_MAX]: `${SUPER_PROJECT_NAME} Max`,
  [UserTier.BUSINESS_SUPERNINJA_PRO]: 'Business Pro',
  [UserTier.BUSINESS_SUPERNINJA_ULTRA]: 'Business Ultra',
  [UserTier.BUSINESS_SUPERNINJA_MAX]: 'Business Max',
};

const BASE_PRO_USAGE_DATASET = {
  '10': {
    planTitle: 'Unlimited Standard plan',
  },
  '30': {
    planTitle: 'Unlimited Ultra plan',
  },
};

export const PRO_USAGE_DATASET: Partial<
  Record<PlanKind, { planTitle: string }>
> = {
  [PlanKind.PRO_10_MONTHLY]: BASE_PRO_USAGE_DATASET['10'],
  [PlanKind.PRO_10_ANNUALLY]: BASE_PRO_USAGE_DATASET['10'],
  [PlanKind.PRO_30_MONTHLY]: BASE_PRO_USAGE_DATASET['30'],
  [PlanKind.PRO_30_ANNUALLY]: BASE_PRO_USAGE_DATASET['30'],
};

export const getMaxUsageFeatures = ({
  requestCount,
  concurrentTasksCount,
  deepResearchType,
  RequestUsageTriggerComponent,
}: PlanUsageFeature & {
  RequestUsageTriggerComponent?: React.ComponentType;
}): PlanFeatureItem[] => [
  {
    iconName: 'superNinjaMainIcon',
    title: (
      <span className={styles.requestUsage}>
        <span>
          <b>{requestCount}</b> {SUPER_PROJECT_NAME} tasks{' '}
        </span>

        {RequestUsageTriggerComponent && <RequestUsageTriggerComponent />}
      </span>
    ),
  },
  {
    Icon: Atom,
    title: (
      <span>
        <b>{deepResearchType}</b> Deep Research{' '}
        <span className={styles.secondary}>(3x-5x Faster)</span>
      </span>
    ),
  },
  {
    Icon: ArrowsClockwise,
    title: (
      <span>
        <b>{concurrentTasksCount}</b> Concurrent {SUPER_PROJECT_NAME} task
        {concurrentTasksCount > 1 ? 's' : ''}
      </span>
    ),
  },
];

export const getNewMaxUsageFeatures = ({
  requestCount,
  concurrentTasksCount,
  RequestUsageTriggerComponent,
}: PlanUsageFeature & {
  RequestUsageTriggerComponent?: JSX.Element;
}): PlanFeatureItem[] => [
  {
    iconName: 'superNinjaMainIcon',
    title: (
      <span className={styles.requestUsage}>
        <span>
          <b>{requestCount}</b> {PROJECT_NAME} tasks{' '}
        </span>

        {RequestUsageTriggerComponent ? RequestUsageTriggerComponent : null}
      </span>
    ),
  },
  {
    Icon: ComputerTower,
    title: (
      <span>
        <b>{concurrentTasksCount}</b> Concurrent Virtual Machine
        {concurrentTasksCount > 1 ? 's' : ''}
      </span>
    ),
  },
];

export const PREMIUM_TEXT_MODELS: PlanFeatureItem = {
  Icon: Check,
  title: (
    <span>
      <b>20+</b> premium text AI models
    </span>
  ),
  modelList: [
    {
      iconName: 'ninjaPixProLogo',
      title: 'Ninja Reasoning 2.0, Turbo 1.0, Apex 1.0',
    },
    {
      iconName: 'chatGPTLogo',
      title: 'GPT 5, GPT 4.1, GPT 5 Mini, GPT 4.1 Mini',
    },
    {
      iconName: 'claudeLogo',
      title: <span>Claude 4 Sonnet</span>,
    },
    {
      iconName: 'googleGeminiProLogo',
      title: 'Gemini 2.5 Pro, Gemini 2.5 Flash',
    },
    {
      iconName: 'deepSeekLogo',
      title: 'DeepSeek R1, DeepSeek V3',
    },
    {
      iconName: 'xaiLogoJSX',
      title: 'Grok 4',
    },
    {
      iconName: 'metaLogoJSX',
      title: 'Llama 4 Scout, Llama 4 Maverick',
    },
    {
      iconName: 'alibabaLogoJSX',
      title: 'Qwen 3 32B (Fast), Qwen 3 235B',
    },
    {
      iconName: 'mistralLogo',
      title: 'Mistral Large, Mistral Small',
    },
    {
      iconName: 'cohereLogo',
      title: 'Cohere Command R+, Cohere Command R',
    },
  ],
};

export const PREMIUM_MEDIA_MODELS: PlanFeatureItem = {
  Icon: Check,
  title: (
    <span>
      <b>10+</b> premium image & video AI models
    </span>
  ),
  modelList: [
    {
      iconName: 'ninjaOutlined',
      title: (
        <span>
          FLUX Schnell, FLUX.1 Kontext Pro,
          <br />
          FLUX 1.1 PRO
        </span>
      ),
    },
    {
      iconName: 'chatGPTLogo',
      title: 'OpenAI GPT Image 1, DALL·E 3',
    },
    {
      iconName: 'bedrockStableLogo',
      title: 'Stable Diffusion Core',
    },
    {
      iconName: 'googleGeminiProLogo',
      title: (
        <span>
          Gemini Imagen 4 Ultra, Imagen 4,
          <br />
          Imagen 4 Fast
        </span>
      ),
    },
    {
      iconName: 'nova',
      title: 'Amazon Nova Canvas, Nova Reel (Video)',
    },
  ],
};

export const PRO_LIMITED_FEATURES: PlanFeatureItem[] = [
  {
    iconName: 'ninjaMainIcon',
    title: (
      <span>
        <b>Unlimited</b> chat tasks
      </span>
    ),
  },
  {
    Icon: Check,
    title: 'Prompt improver',
  },
  {
    Icon: Code,
    title: 'Code generation & debug',
  },
  {
    Icon: Images,
    title: 'Image generation',
  },
  {
    Icon: PencilSimpleLine,
    title: 'Creative and professional writing',
  },
  {
    Icon: ListMagnifyingGlass,
    title: 'Fast search',
  },
  {
    Icon: Waveform,
    title: 'Generate podcast',
  },
];

export const PRO_EXTENDED_FEATURES: PlanFeatureItem[] = [
  {
    iconName: 'ninjaMainIcon',
    title: (
      <span>
        <b>Unlimited</b> chat tasks
      </span>
    ),
  },
  {
    Icon: Atom,
    title: 'Deep Research',
  },
  PREMIUM_TEXT_MODELS,
  PREMIUM_MEDIA_MODELS,
  {
    Icon: Check,
    title: 'Document & code editor',
  },
  {
    Icon: Check,
    title: 'Compare mode',
  },
  {
    iconName: 'ninjaMainIcon',
    title: 'File upload up to 2M words',
  },
  {
    Icon: Video,
    title: 'Video generation',
  },
];

export const MAX_FEATURES: PlanFeatureItem[] = [
  {
    Icon: CodeBlock,
    title: 'End-to-end coding applications',
  },
  {
    iconName: 'githubLogo',
    title: (
      <span>
        <span>GitHub integration</span>
      </span>
    ),
  },
  {
    Icon: Browsers,
    title: 'Website creation',
    description: 'Create webpages & deploy with public URL',
  },
  {
    Icon: Files,
    title: 'Document creation',
    description: 'Slides, documents and spreadsheets',
  },
  {
    Icon: GraduationCap,
    title: 'Advanced research & insight',
  },
  {
    Icon: ChartLine,
    title: 'Data analysis and visualization',
  },
  {
    Icon: Key,
    title: (
      <span>
        <span>Custom API integrations</span>
        <span className="nj-feature-secondary-item"> (coming soon)</span>
      </span>
    ),
  },
  {
    Icon: Folder,
    title: (
      <span>
        <span>Create project</span>
      </span>
    ),
  },
  PREMIUM_TEXT_MODELS,
  PREMIUM_MEDIA_MODELS,
];

export const BUSINESS_FEATURES: PlanFeatureItem[] = [
  {
    Icon: Check,
    title: 'Opt out of data training',
  },
  {
    Icon: Check,
    title: 'Workspace management',
  },
  {
    Icon: Check,
    title: 'Pay-per-use API for Ninja models',
  },
];

export const PLAN_KINDS_WITH_FAMILY_MAP: Record<PlanKind, PlanFamily> = {
  [PlanKind.PRO_10_MONTHLY]: PlanFamily.PRO,
  [PlanKind.PRO_10_ANNUALLY]: PlanFamily.PRO,
  [PlanKind.PRO_30_MONTHLY]: PlanFamily.PRO,
  [PlanKind.PRO_30_ANNUALLY]: PlanFamily.PRO,
  [PlanKind.MAX_05_MONTHLY]: PlanFamily.MAX,
  [PlanKind.MAX_05_ANNUALLY]: PlanFamily.MAX,
  [PlanKind.MAX_10_MONTHLY]: PlanFamily.MAX,
  [PlanKind.MAX_10_ANNUALLY]: PlanFamily.MAX,
  [PlanKind.MAX_20_MONTHLY]: PlanFamily.MAX,
  [PlanKind.MAX_20_ANNUALLY]: PlanFamily.MAX,
  [PlanKind.MAX_30_MONTHLY]: PlanFamily.MAX,
  [PlanKind.MAX_30_ANNUALLY]: PlanFamily.MAX,
  [PlanKind.BUSINESS_10_MONTHLY]: PlanFamily.BUSINESS,
  [PlanKind.BUSINESS_10_ANNUALLY]: PlanFamily.BUSINESS,
  [PlanKind.BUSINESS_20_MONTHLY]: PlanFamily.BUSINESS,
  [PlanKind.BUSINESS_20_ANNUALLY]: PlanFamily.BUSINESS,
  [PlanKind.BUSINESS_30_MONTHLY]: PlanFamily.BUSINESS,
  [PlanKind.BUSINESS_30_ANNUALLY]: PlanFamily.BUSINESS,
  [PlanKind.PRO_MONTHLY]: PlanFamily.UNDEFINED,
  [PlanKind.PRO_ANNUALLY]: PlanFamily.UNDEFINED,
  [PlanKind.ULTRA_MONTHLY]: PlanFamily.UNDEFINED,
  [PlanKind.ULTRA_ANNUALLY]: PlanFamily.UNDEFINED,
  [PlanKind.STANDARD_MONTHLY]: PlanFamily.UNDEFINED,
  [PlanKind.STANDARD_ANNUALLY]: PlanFamily.UNDEFINED,
  [PlanKind.SUPERNINJA_ULTRA_MONTHLY_10]: PlanFamily.UNDEFINED,
  [PlanKind.SUPERNINJA_ULTRA_MONTHLY_20]: PlanFamily.UNDEFINED,
  [PlanKind.SUPERNINJA_ULTRA_MONTHLY]: PlanFamily.UNDEFINED,
  [PlanKind.SUPERNINJA_ULTRA_ANNUALLY_10]: PlanFamily.UNDEFINED,
  [PlanKind.SUPERNINJA_ULTRA_ANNUALLY_20]: PlanFamily.UNDEFINED,
  [PlanKind.SUPERNINJA_ULTRA_ANNUALLY]: PlanFamily.UNDEFINED,
  [PlanKind.TEAMS_PRO_MONTHLY]: PlanFamily.UNDEFINED,
  [PlanKind.TEAMS_PRO_ANNUALLY]: PlanFamily.UNDEFINED,
  [PlanKind.TEAMS_ULTRA_MONTHLY]: PlanFamily.UNDEFINED,
  [PlanKind.TEAMS_ULTRA_ANNUALLY]: PlanFamily.UNDEFINED,
  [PlanKind.TEAMS_SUPERNINJA_ULTRA_MONTHLY_10]: PlanFamily.UNDEFINED,
  [PlanKind.TEAMS_SUPERNINJA_ULTRA_MONTHLY_20]: PlanFamily.UNDEFINED,
  [PlanKind.TEAMS_SUPERNINJA_ULTRA_MONTHLY]: PlanFamily.UNDEFINED,
  [PlanKind.TEAMS_SUPERNINJA_ULTRA_ANNUALLY_10]: PlanFamily.UNDEFINED,
  [PlanKind.TEAMS_SUPERNINJA_ULTRA_ANNUALLY_20]: PlanFamily.UNDEFINED,
  [PlanKind.TEAMS_SUPERNINJA_ULTRA_ANNUALLY]: PlanFamily.UNDEFINED,
  [PlanKind.TEAMS_STANDARD_MONTHLY]: PlanFamily.UNDEFINED,
  [PlanKind.TEAMS_STANDARD_ANNUALLY]: PlanFamily.UNDEFINED,
  [PlanKind.CREDIT_1_MONTHLY]: PlanFamily.CREDIT,
  [PlanKind.CREDIT_1_ANNUALLY]: PlanFamily.CREDIT,
  [PlanKind.CREDIT_5_MONTHLY]: PlanFamily.CREDIT,
  [PlanKind.CREDIT_5_ANNUALLY]: PlanFamily.CREDIT,
};

export const ALL_PRO_MODELS: { icon: IconName; models: string }[] = [
  {
    icon: 'chatGPTLogo',
    models: 'GPT 5.4, GPT Image 1.5',
  },
  {
    icon: 'claudeLogo',
    models: 'Claude Sonnet 4.6, Claude Haiku 4.5',
  },
  {
    icon: 'deepSeekLogo',
    models: 'DeepSeek v3.1',
  },
  {
    icon: 'googleGeminiProLogo',
    models: 'Gemini 3.1 Pro, Gemini 3.0 Flash, Nano Banana',
  },
  {
    icon: 'ninjaOutlined',
    models: 'FLUX Schnell',
  },
];

export const ALL_STANDARD_MODELS: { icon: IconName; models: string }[] = [
  {
    icon: 'chatGPTLogo',
    models: 'GPT 5.4, GPT Image 1.5',
  },
  {
    icon: 'claudeLogo',
    models: 'Claude Sonnet 4.6, Claude Haiku 4.5',
  },
  {
    icon: 'deepSeekLogo',
    models: 'DeepSeek v3.1',
  },
  {
    icon: 'googleGeminiProLogo',
    models: 'Gemini 3.0 Flash',
  },
  {
    icon: 'ninjaOutlined',
    models: 'FLUX Schnell',
  },
];

export const ALL_ULTRA_MODELS: { icon: IconName; models: string }[] = [
  {
    icon: 'chatGPTLogo',
    models: 'GPT 5.4, GPT Image 1.5',
  },
  {
    icon: 'claudeLogo',
    models: 'Claude Sonnet 4.6, Claude Haiku 4.5',
  },
  {
    icon: 'deepSeekLogo',
    models: 'DeepSeek v3.1',
  },
  {
    icon: 'googleGeminiProLogo',
    models: 'Gemini 3.1 Pro, Gemini 3.0 Flash, Nano Banana',
  },
  {
    icon: 'xaiLogoJSX',
    models: 'Grok Code Fast',
  },
  {
    icon: 'ninjaOutlined',
    models: 'FLUX Schnell',
  },
];

export const ALL_MAX_MODELS: { icon: IconName; models: string }[] = [
  {
    icon: 'ninjaPixProLogo',
    models: 'FLUX Schnell, FLUX.1 Kontext Pro, FLUX 1.1 PRO',
  },
  {
    icon: 'chatGPTLogo',
    models:
      'GPT-5.1, GPT-5, GPT-5 Mini, GPT-5 High Thinking, o3, GPT-4.1, GPT-4.1 Mini, GPT Image 1, DALL·E 3',
  },
  {
    icon: 'claudeLogo',
    models: 'Claude 4.6 Sonnet, Claude Opus 4.6, Claude 4 Sonnet',
  },
  {
    icon: 'googleGeminiProLogo',
    models:
      'Gemini 3.0 Pro (Preview), Gemini Image Pro 3.0 (Preview), Gemini 2.5 Pro, Gemini 2.5 Flash, Imagen 4 Ultra, Imagen 4, Imagen 4 Fast',
  },
  {
    icon: 'deepSeekLogo',
    models: 'DeepSeek R1, DeepSeek V3',
  },
  {
    icon: 'xaiLogoJSX',
    models: 'Grok 4, Grok Code Fast',
  },
  {
    icon: 'metaLogoJSX',
    models: 'Llama 4 Scout, Llama 4 Maverick',
  },
  {
    icon: 'alibabaLogoJSX',
    models: 'Qwen 3 32B (Cerebras Ultra Fast), Qwen 3 32B (Groq Fast)',
  },
  {
    icon: 'mistralLogo',
    models: 'Mistral Large, Mistral Small',
  },
  {
    icon: 'cohereLogo',
    models: 'Cohere Command R+, Cohere Command R',
  },
  {
    icon: 'nova',
    models:
      'Amazon Nova Pro, Amazon Nova Lite, Amazon Nova Micro, Amazon Nova Canvas',
  },
  {
    icon: 'bedrockStableLogo',
    models: 'Stable Diffusion Core',
  },
];

export const ADDITIONAL_SN_STANDARD_MODELS: PlanFeatureItem[] = [
  {
    Icon: Check,
    title: (
      <span>
        <b>25+</b> premium text AI models
      </span>
    ),
    modelList: [
      {
        iconName: 'ninjaPixProLogo',
        title: 'Ninja Reasoning 2.0, Turbo 1.0, Apex 1.0',
      },
      {
        iconName: 'chatGPTLogo',
        title: 'GPT 5.1, GPT 5, GPT 4.1, GPT 4.1 Mini',
      },
      {
        iconName: 'claudeLogo',
        title: <span>Claude 4 Sonnet</span>,
      },
      {
        iconName: 'googleGeminiProLogo',
        title: 'Gemini 2.5 Flash',
      },
      {
        iconName: 'deepSeekLogo',
        title: 'DeepSeek R1, DeepSeek V3',
      },
      {
        iconName: 'xaiLogoJSX',
        title: 'Grok 4',
      },
      {
        iconName: 'metaLogoJSX',
        title: 'Llama 4 Scout, Llama 4 Maverick',
      },
      {
        iconName: 'alibabaLogoJSX',
        title: 'Qwen 3 32B (Fast), Qwen 3 235B',
      },
      {
        iconName: 'mistralLogo',
        title: 'Mistral Large, Mistral Small',
      },
      {
        iconName: 'cohereLogo',
        title: 'Cohere Command R+, Cohere Command R',
      },
    ],
  },
];

export const ADDITIONAL_SN_IMAGE_MODELS: PlanFeatureItem[] = [
  {
    Icon: Check,
    title: (
      <span>
        <b>10+</b> premium image & video AI models
      </span>
    ),
    modelList: [
      {
        iconName: 'ninjaOutlined',
        title: (
          <span>
            FLUX Schnell, FLUX.1 Kontext Pro,
            <br />
            FLUX 1.1 PRO
          </span>
        ),
      },
      {
        iconName: 'chatGPTLogo',
        title: 'OpenAI GPT Image 1, DALL·E 3',
      },
      {
        iconName: 'bedrockStableLogo',
        title: 'Stable Diffusion Core',
      },
      {
        iconName: 'googleGeminiProLogo',
        title: (
          <span>
            Gemini Imagen 4 Ultra, Imagen 4,
            <br />
            Imagen 4 Fast
          </span>
        ),
      },
      {
        iconName: 'nova',
        title: 'Amazon Nova Canvas, Nova Reel (Video)',
      },
    ],
  },
];

export const COMMON_SN_STANDARD_FEATURES = [
  {
    Icon: Check,
    title: 'GitHub integration',
  },
  {
    Icon: Check,
    title: 'Create projects',
  },
];

export const ADDITIONAL_SN_STANDARD_FEATURES: PlanFeatureItem[] = [
  ...ADDITIONAL_SN_STANDARD_MODELS,
  ...ADDITIONAL_SN_IMAGE_MODELS,
  ...COMMON_SN_STANDARD_FEATURES,
];

export const ADDITIONAL_SN_TEXT_MODELS: PlanFeatureItem[] = [
  {
    Icon: Check,
    title: (
      <span>
        <b>30+</b> premium text AI models
      </span>
    ),
    modelList: [
      {
        iconName: 'ninjaPixProLogo',
        title: 'Ninja Reasoning 2.0, Turbo 1.0, Apex 1.0',
      },
      {
        iconName: 'chatGPTLogo',
        title: 'GPT-5, GPT-5 Pro, GPT 4.1, GPT 4.1 Mini, o3',
      },
      {
        iconName: 'claudeLogo',
        title: 'Claude Opus 4.1, Claude 4 Sonnet',
      },
      {
        iconName: 'googleGeminiProLogo',
        title: 'Gemini 2.5 Pro, Gemini 2.5 Pro thinking, Gemini 2.5 Flash',
      },
      {
        iconName: 'deepSeekLogo',
        title: 'DeepSeek R1, DeepSeek V3',
      },
      {
        iconName: 'xaiLogoJSX',
        title: 'Grok 4',
      },
      {
        iconName: 'kimiLogo',
        title: 'Kimi K2 (1T Param)',
      },
      {
        iconName: 'metaLogoJSX',
        title: 'Llama 4 Scout, Llama 4 Maverick',
      },
      {
        iconName: 'alibabaLogoJSX',
        title: 'Qwen 3 32B (Fast), Qwen 3 235B',
      },
      {
        iconName: 'mistralLogo',
        title: 'Mistral Large, Mistral Small',
      },
      {
        iconName: 'cohereLogo',
        title: 'Cohere Command R+, Cohere Command R',
      },
    ],
  },
];

export const COMMON_SN_FEATURES = [
  {
    Icon: Check,
    title: 'VS code editor',
  },
  {
    Icon: Check,
    title: 'Image editor',
  },
  {
    Icon: Check,
    title: 'Website editor',
  },
  {
    Icon: Check,
    title: 'Text editor',
  },
  {
    Icon: Check,
    title: 'GitHub integration',
  },
  {
    Icon: Check,
    title: 'Create projects',
  },
];

export const ADDITIONAL_SN_FEATURES: PlanFeatureItem[] = [
  ...ADDITIONAL_SN_TEXT_MODELS,
  ...ADDITIONAL_SN_IMAGE_MODELS,
  ...COMMON_SN_FEATURES,
];

export const COMMON_SN_BUSINESS_FEATURES = [
  {
    Icon: Check,
    title: 'Opt out of data training',
  },
  {
    Icon: Check,
    title: 'Workspace management',
  },
];

export const ADDITIONAL_SN_BUSINESS_FEATURES: PlanFeatureItem[] = [
  ...ADDITIONAL_SN_FEATURES,
  ...COMMON_SN_BUSINESS_FEATURES,
];

export const NEW_SN_STANDARD_FEATURES: PlanFeatureItem[] = [
  {
    Icon: TextAlignLeft,
    title: (
      <span>
        <b>1M</b> Token context
      </span>
    ),
  },
  {
    Icon: HardDrives,
    title: (
      <span>
        <b>1GB</b> Storage - Ninja drive{' '}
        <span className="nj-feature-secondary-item"> (coming soon)</span>
      </span>
    ),
  },
  {
    Icon: Timer,
    title: (
      <span>
        <span>1 Scheduled tasks</span>
        <span className="nj-feature-secondary-item"> (coming soon)</span>
      </span>
    ),
  },
  {
    Icon: Sparkle,
    title: (
      <span>
        <span>5 MCP data sources</span>
        <span className="nj-feature-secondary-item"> (coming soon)</span>
      </span>
    ),
  },
];

export const NEW_SN_PRO_FEATURES: PlanFeatureItem[] = [
  {
    Icon: ArrowsClockwise,
    title: (
      <span>
        <b>Undo & redo</b> AI actions{' '}
        <span className="nj-feature-secondary-item"> (coming soon)</span>
      </span>
    ),
  },
  {
    Icon: TextAlignLeft,
    title: (
      <span>
        <b>2M</b> Token context
      </span>
    ),
  },
  {
    Icon: HardDrives,
    title: (
      <span>
        <b>5GB</b> Storage - Ninja drive{' '}
        <span className="nj-feature-secondary-item"> (coming soon)</span>
      </span>
    ),
  },
  {
    Icon: Timer,
    title: (
      <span>
        <span>2 Scheduled tasks</span>
        <span className="nj-feature-secondary-item"> (coming soon)</span>
      </span>
    ),
  },
  {
    Icon: Sparkle,
    title: (
      <span>
        <span>5 MCP data sources</span>
        <span className="nj-feature-secondary-item"> (coming soon)</span>
      </span>
    ),
  },
];

export const NEW_SN_ULTRA_FEATURES: PlanFeatureItem[] = [
  {
    Icon: ArrowsClockwise,
    title: (
      <span>
        <b>Undo & redo</b> AI actions{' '}
        <span className="nj-feature-secondary-item"> (coming soon)</span>
      </span>
    ),
  },
  {
    Icon: TextAlignLeft,
    title: (
      <span>
        <b>3M</b> Token context
      </span>
    ),
  },
  {
    Icon: HardDrives,
    title: (
      <span>
        <b>10GB</b> Storage - Ninja drive{' '}
        <span className="nj-feature-secondary-item"> (coming soon)</span>
      </span>
    ),
  },
  {
    Icon: Timer,
    title: (
      <span>
        <span>5 Scheduled tasks</span>
        <span className="nj-feature-secondary-item"> (coming soon)</span>
      </span>
    ),
  },
  {
    Icon: Sparkle,
    title: (
      <span>
        <span>10 MCP data sources</span>
        <span className="nj-feature-secondary-item"> (coming soon)</span>
      </span>
    ),
  },
];

export const NEW_SN_MAX_FEATURES: PlanFeatureItem[] = [
  {
    Icon: ArrowsClockwise,
    title: (
      <span>
        <b>Undo & redo</b> AI actions{' '}
        <span className="nj-feature-secondary-item"> (coming soon)</span>
      </span>
    ),
  },
  {
    Icon: TextAlignLeft,
    title: (
      <span>
        <b>Infinite</b> context
      </span>
    ),
  },
  {
    Icon: HardDrives,
    title: (
      <span>
        <b>100GB</b> Storage - Ninja drive{' '}
        <span className="nj-feature-secondary-item"> (coming soon)</span>
      </span>
    ),
  },
  {
    Icon: Timer,
    title: (
      <span>
        <span>10 Scheduled tasks</span>
        <span className="nj-feature-secondary-item"> (coming soon)</span>
      </span>
    ),
  },
  {
    Icon: Sparkle,
    title: (
      <span>
        <span>50 MCP data sources</span>
        <span className="nj-feature-secondary-item"> (coming soon)</span>
      </span>
    ),
  },
];

export const PLANS_DETAILS = [
  {
    type: 'Available tasks',
    description: '',
    values: ['150', '300', 'Unlimited'],
  },
  {
    type: 'Standard',
    description: 'AI that balances quality & speed',
    values: ['60', '120', 'Unlimited'],
  },
  {
    type: 'Complex',
    description: 'High-quality AI for sophisticated tasks',
    values: ['45', '90', '225'],
  },
  {
    type: 'Fast',
    description: 'AI at lighting speed without compromising quality',
    values: ['45', '90', '225'],
  },
];

const iconSize = 12;

export const StandardDefaultModelsWrapper = () => {
  return (
    <div className={styles.requestTableModelsWrapper}>
      <div className={styles.requestTableModelRow}>
        <span>Default:</span>
        <div className={styles.requestTableModel}>
          <Icon type="zaiLogo" size={iconSize} />
          <span>GLM 4.7</span>
        </div>
      </div>
      <div className={styles.requestTableModelRow}>
        <span>Deep Coder:</span>
        <div className={styles.requestTableModel}>
          <Icon type="zaiLogo" size={iconSize} />
          <span>GLM 4.7</span>
        </div>
      </div>
    </div>
  );
};

export const COMPLEX_DEFAULT_MODELS = (
  <div className={styles.requestTableModelsWrapper}>
    <div className={styles.requestTableModelRow}>
      <span>Default:</span>
      <div className={styles.requestTableModel}>
        <Icon type="claudeLogo" size={iconSize} />
        <span>Sonnet 4.6</span>
      </div>
    </div>
    <div className={styles.requestTableModelRow}>
      <span>Deep Coder:</span>
      <div className={styles.requestTableModel}>
        <Icon type="claudeLogo" size={iconSize} />
        <span>Sonnet 4.6</span>
      </div>
    </div>
  </div>
);

export const FAST_DEFAULT_MODELS = (
  <div className={styles.requestTableModelsWrapper}>
    <div className={styles.requestTableModelRow}>
      <span>Default:</span>
      <div className={styles.requestTableModel}>
        <Icon type="zaiLogo" size={iconSize} />
        <span>GLM 4.6</span>
      </div>
    </div>
    <div className={styles.requestTableModelRow}>
      <span>Deep Research:</span>
      <div className={styles.requestTableModel}>
        <Icon type="alibabaLogoJSX" size={iconSize} />
        <span>
          Qwen3–235B<sup>*</sup>
        </span>
      </div>
    </div>
    <div className={styles.requestTableModelRow}>
      <span>Deep Coder:</span>
      <div className={styles.requestTableModel}>
        <Icon type="zaiLogo" size={iconSize} />
        <span>GLM 4.6</span>
      </div>
    </div>
    <div className={styles.requestTableModelRow}>
      <div className={styles.requestTableHintRow}>
        <span>
          <sup>*</sup>Powered by Cerebras
        </span>
        <Icon type="cerebrasLogoJSX" size={SVG_SIZE_S} />
      </div>
    </div>
  </div>
);

export type PlansDetailsDataType = {
  type: string;
  description: string;
  values: string[];
  bottomComponent?: JSX.Element;
  fieldName: keyof SnEntitlementDetailLimits | 'total';
};

export const NEW_PLANS_DETAILS: PlansDetailsDataType[] = [
  {
    type: 'Available tasks',
    description: '',
    values: ['40', '80', '160', 'Unlimited'],
    fieldName: 'total',
  },
  {
    type: 'Standard',
    description: 'Balanced for quality & speed',
    values: ['10', '20', '40', 'Unlimited'],
    bottomComponent: <StandardDefaultModelsWrapper />,
    fieldName: 'standard_requests',
  },
  {
    type: 'Complex',
    description: 'Highest quality LLM for complex tasks',
    values: ['20', '40', '80', '300'],
    bottomComponent: COMPLEX_DEFAULT_MODELS,
    fieldName: 'complex_requests',
  },
  {
    type: 'Fast (Up to 1,000 tokens/sec)',
    description: 'Fastest agent  in the world',
    values: ['10', '20', '40', 'Unlimited'],
    bottomComponent: FAST_DEFAULT_MODELS,
    fieldName: 'fast_requests',
  },
];

export const NEW_PLANS_BUSINESS_DETAILS: PlansDetailsDataType[] = [
  {
    type: 'Available tasks',
    description: '',
    values: ['80', '160', 'Unlimited'],
    fieldName: 'total',
  },
  {
    type: 'Standard',
    description: 'Balanced for quality & speed',
    values: ['20', '40', 'Unlimited'],
    bottomComponent: <StandardDefaultModelsWrapper />,
    fieldName: 'standard_requests',
  },
  {
    type: 'Complex',
    description: 'Highest quality LLM for complex tasks',
    values: ['40', '80', '300'],
    bottomComponent: COMPLEX_DEFAULT_MODELS,
    fieldName: 'complex_requests',
  },
  {
    type: 'Fast (Up to 3,000 tokens/sec)',
    description: 'Fastest agent  in the world',
    values: ['20', '40', 'Unlimited'],
    bottomComponent: FAST_DEFAULT_MODELS,
    fieldName: 'fast_requests',
  },
];

/**
 * @deprecated
 */
export const NEW_PLANS_VARIANTS = [
  PlanVariant._20250917,
  PlanVariant._20251102,
];

export const PLAN_KIND_TITLE_MAP_V2: Partial<
  Record<
    PlanKind,
    { fullTitle: string; shortTitle: string; description: string }
  >
> = {
  [PlanKind.MAX_05_MONTHLY]: {
    fullTitle: 'Standard',
    shortTitle: 'Standard',
    description:
      'Get everyday help from Ninja to move faster on any task or project.',
  },
  [PlanKind.MAX_05_ANNUALLY]: {
    fullTitle: 'Standard',
    shortTitle: 'Standard',
    description:
      'Get everyday help from Ninja to move faster on any task or project.',
  },
  [PlanKind.MAX_10_MONTHLY]: {
    fullTitle: 'Pro',
    shortTitle: 'Pro',
    description:
      'Get higher access to new features and premium models to boost productivity.',
  },
  [PlanKind.MAX_10_ANNUALLY]: {
    fullTitle: 'Pro',
    shortTitle: 'Pro',
    description:
      'Get higher access to new features and premium models to boost productivity.',
  },
  [PlanKind.MAX_20_MONTHLY]: {
    fullTitle: 'Ultra',
    shortTitle: 'Ultra',
    description:
      'Get increased usage and enhanced throughput for high-demand tasks.',
  },
  [PlanKind.MAX_20_ANNUALLY]: {
    fullTitle: 'Ultra',
    shortTitle: 'Ultra',
    description:
      'Get increased usage and enhanced throughput for high-demand tasks.',
  },
  [PlanKind.MAX_30_MONTHLY]: {
    fullTitle: 'Max',
    shortTitle: 'Max',
    description:
      'Unlock the highest level of access to the best of Ninja and exclusive features.',
  },
  [PlanKind.MAX_30_ANNUALLY]: {
    fullTitle: 'Max',
    shortTitle: 'Max',
    description:
      'Unlock the highest level of access to the best of Ninja and exclusive features.',
  },
  [PlanKind.BUSINESS_10_MONTHLY]: {
    fullTitle: 'Business Pro',
    shortTitle: 'Pro',
    description:
      'Get higher access to new features and premium models to boost productivity.',
  },
  [PlanKind.BUSINESS_10_ANNUALLY]: {
    fullTitle: 'Business Pro',
    shortTitle: 'Pro',
    description:
      'Get higher access to new features and premium models to boost productivity.',
  },
  [PlanKind.BUSINESS_20_MONTHLY]: {
    fullTitle: 'Business Ultra',
    shortTitle: 'Ultra',
    description:
      'Get increased usage and enhanced throughput for high-demand tasks.',
  },
  [PlanKind.BUSINESS_20_ANNUALLY]: {
    fullTitle: 'Business Ultra',
    shortTitle: 'Ultra',
    description:
      'Get increased usage and enhanced throughput for high-demand tasks.',
  },
  [PlanKind.BUSINESS_30_MONTHLY]: {
    fullTitle: 'Business Max',
    shortTitle: 'Max',
    description:
      'Unlock the highest level of access to the best of Ninja and exclusive features.',
  },
  [PlanKind.BUSINESS_30_ANNUALLY]: {
    fullTitle: 'Business Max',
    shortTitle: 'Max',
    description:
      'Unlock the highest level of access to the best of Ninja and exclusive features.',
  },
};

export const ADDITIONAL_COMMON_FEATURES_V2: PlanFeatureItem[] = [
  {
    Icon: Check,
    title: 'VS code editor',
  },
  {
    Icon: Check,
    title: 'Image editor',
  },
  {
    Icon: Check,
    title: 'Website editor',
  },
  {
    Icon: Check,
    title: 'Text editor',
  },
  {
    Icon: Check,
    title: 'GitHub integration',
  },
  {
    Icon: Check,
    title: 'Create projects',
  },
];

export const MODEL_LIST_V2: PlanModelItem[] = [
  {
    iconName: 'chatGPTLogo',
    title: 'GPT 5.4',
  },
  {
    iconName: 'claudeLogo',
    title: 'Claude Sonnet 4.6',
  },
  {
    iconName: 'googleGeminiProLogo',
    title: 'Gemini 3.1 Pro',
  },
  {
    iconName: 'claudeLogo',
    title: 'Claude Haiku 4.5',
  },
  {
    iconName: 'deepSeekLogo',
    title: 'DeepSeek v3.1',
  },
  {
    iconName: 'googleGeminiProLogo',
    title: 'Gemini 3.0 Flash',
  },
];

export const MODEL_LIST_PRO_V2: PlanModelItem[] = [
  {
    iconName: 'chatGPTLogo',
    title: 'GPT-5.4',
  },
  {
    iconName: 'claudeLogo',
    title: 'Claude Opus 4.6',
  },
  {
    iconName: 'chatGPTLogo',
    title: 'o3',
  },
  {
    iconName: 'googleGeminiProLogo',
    title: 'Gemini 3.0 Pro (Preview)',
  },
  {
    iconName: 'xaiLogoJSX',
    title: 'Grok Code Fast',
  },
  {
    iconName: 'googleGeminiProLogo',
    title: 'Imagen 4 Ultra',
  },
];

export const MODEL_LIST_LIMITED_V2: PlanModelItem[] = [
  {
    iconName: 'chatGPTLogo',
    title: 'GPT 5.4',
  },
  {
    iconName: 'claudeLogo',
    title: 'Claude Sonnet 4.6',
  },
  {
    iconName: 'googleGeminiProLogo',
    title: 'Gemini 3.1 Pro',
  },
  {
    iconName: 'claudeLogo',
    title: 'Claude Haiku 4.5',
  },
  {
    iconName: 'deepSeekLogo',
    title: 'DeepSeek v3.1',
  },
  {
    iconName: 'googleGeminiProLogo',
    title: 'Gemini 3.0 Flash',
  },
];

export const MODEL_LIST_LIMITED_STANDARD_V2: PlanModelItem[] = [
  {
    iconName: 'chatGPTLogo',
    title: 'GPT 5.4',
  },
  {
    iconName: 'claudeLogo',
    title: 'Claude Sonnet 4.6',
  },
  {
    iconName: 'claudeLogo',
    title: 'Claude Haiku 4.5',
  },
  {
    iconName: 'deepSeekLogo',
    title: 'DeepSeek v3.1',
  },
  {
    iconName: 'googleGeminiProLogo',
    title: 'Gemini 3.0 Flash',
  },
  {
    iconName: 'chatGPTLogo',
    title: 'GPT Image 1.5',
  },
];

export const ADDITIONAL_STANDARD_FEATURES_V2: PlanFeatureItem[] = [
  {
    Icon: Check,
    title: (
      <TooltipWithModels
        models={ALL_STANDARD_MODELS}
        id="Standard_Plan_Tooltip"
        hoveredText={
          <span className={styles.textWithUnderline}>
            Text, & image AI models
          </span>
        }
      />
    ),
    modelList: [...MODEL_LIST_LIMITED_STANDARD_V2],
  },
  {
    Icon: Check,
    title: (
      <span>
        <b>100</b> messages daily, unthrottled
      </span>
    ),
  },
  {
    Icon: Check,
    title: 'GitHub integration',
  },
  {
    Icon: Check,
    title: 'Create projects',
  },
];

export const ADDITIONAL_PRO_FEATURES_V2: PlanFeatureItem[] = [
  {
    Icon: Check,
    title: (
      <TooltipWithModels
        models={ALL_PRO_MODELS}
        id="Pro_Plan_Tooltip"
        hoveredText={
          <span className={styles.textWithUnderline}>
            Text, & image AI models
          </span>
        }
      />
    ),
    modelList: [...MODEL_LIST_LIMITED_V2],
  },
  {
    Icon: Check,
    title: (
      <span>
        <b>200</b> messages daily, unthrottled
      </span>
    ),
  },
  ...ADDITIONAL_COMMON_FEATURES_V2,
];

export const ADDITIONAL_ULTRA_FEATURES_V2: PlanFeatureItem[] = [
  {
    Icon: Check,
    title: (
      <TooltipWithModels
        models={ALL_ULTRA_MODELS}
        id="Ultra_Plan_Tooltip"
        hoveredText={
          <span className={styles.textWithUnderline}>
            <b>All available</b> text, & image AI models
          </span>
        }
      />
    ),
    modelList: [...MODEL_LIST_V2],
  },
  {
    Icon: Check,
    title: (
      <span>
        <b>500</b> messages daily, unthrottled
      </span>
    ),
  },
  ...ADDITIONAL_COMMON_FEATURES_V2,
];

export const ADDITIONAL_MAX_FEATURES_V2: PlanFeatureItem[] = [
  {
    Icon: Check,
    title: (
      <TooltipWithModels
        models={ALL_ULTRA_MODELS}
        id="Max_Plan_Tooltip"
        hoveredText={
          <span className={styles.textWithUnderline}>
            <b>All available</b> text, & image AI models
          </span>
        }
      />
    ),
    modelList: [...MODEL_LIST_V2],
  },
  {
    Icon: Check,
    title: (
      <span>
        <b>1,000+</b> messages daily, unthrottled
      </span>
    ),
  },
  ...ADDITIONAL_COMMON_FEATURES_V2,
];

export const INDIVIDUAL_FEATURES_V2: PlanFeatureItem[] = [
  {
    Icon: CirclesThreePlus,
    title: 'Storage, Scheduled tasks & MCPs',
  },
];

export const BUSINESS_FEATURES_V2: PlanFeatureItem[] = [
  ...INDIVIDUAL_FEATURES_V2,
  {
    Icon: Shield,
    title: 'Opt out of data training',
  },
  {
    Icon: UsersThree,
    title: 'Workspace management',
  },
];

export const STANDARD_FEATURES_V2: PlanFeatureItem[] = [
  {
    Icon: TextAlignLeft,
    title: (
      <span>
        <b>1M context window</b>
      </span>
    ),
  },
  {
    Icon: CirclesThreePlus,
    title: 'Storage & Scheduled tasks',
  },
];

export const PRO_FEATURES_V2: PlanFeatureItem[] = [
  {
    Icon: ArrowsClockwise,
    title: (
      <span>
        <span>
          <b>Undo & redo</b> AI actions
        </span>
        <span className={styles.secondary}> (coming soon)</span>
      </span>
    ),
  },
  {
    Icon: TextAlignLeft,
    title: (
      <span>
        <b>1M context window</b>
      </span>
    ),
  },
];

export const MAX_FEATURES_V2: PlanFeatureItem[] = [
  {
    Icon: ArrowsClockwise,
    title: (
      <span>
        <span>
          <b>Undo & redo</b> across any AI actions
        </span>
        <span className={styles.secondary}> (coming soon)</span>
      </span>
    ),
  },
  {
    Icon: TextAlignLeft,
    title: (
      <span>
        <b>Intelligent infinite context</b>
      </span>
    ),
  },
];

export const ULTRA_FEATURES_V2: PlanFeatureItem[] = [
  {
    Icon: ArrowsClockwise,
    title: (
      <span>
        <span>
          <b>Undo & redo</b> across any AI actions
        </span>
        <span className={styles.secondary}> (coming soon)</span>
      </span>
    ),
  },
  {
    Icon: TextAlignLeft,
    title: (
      <span>
        <b>3M</b> Token context
      </span>
    ),
  },
];

export const STARTING_PRICE = 16;
